/**
 * App Store
 * Centralized state management for application-level state
 * (document, rule-check results, parse settings)
 */

import { createStore } from 'zustand/vanilla';
import { devtools, persist } from 'zustand/middleware';
import type { Document } from '../core/document-manager';
import type { CheckRulesResponse } from '../core/gateway/types';
import {
  DEFAULT_PARSE_SETTINGS,
  type ParseSettings,
} from '../core/parse-options-mapper';

interface AppState {
  // State
  currentDocument: Document | null;
  relationsDomain: string;
  lastCheckResponse: CheckRulesResponse | null;
  /** Map of statement IRI to 1-based sequential number, set after highlighting. */
  statementNumberMap: Map<string, number>;
  parseSettings: ParseSettings;

  // Actions
  setDocument: (document: Document | null) => void;
  setLastCheckResponse: (response: CheckRulesResponse | null) => void;
  setStatementNumberMap: (map: Map<string, number>) => void;
  setParseSettings: (settings: Partial<ParseSettings>) => void;
  reset: () => void;

  // Computed (as functions for Zustand)
  hasDocument: () => boolean;
}

const initialState = {
  currentDocument: null,
  relationsDomain: 'construction',
  lastCheckResponse: null as CheckRulesResponse | null,
  statementNumberMap: new Map<string, number>(),
  parseSettings: DEFAULT_PARSE_SETTINGS,
};

export const useAppStore = createStore<AppState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        ...initialState,

        // Actions
        setDocument: (currentDocument) =>
          set({ currentDocument }, false, 'setDocument'),

        setLastCheckResponse: (lastCheckResponse) =>
          set({ lastCheckResponse }, false, 'setLastCheckResponse'),

        setStatementNumberMap: (statementNumberMap) =>
          set({ statementNumberMap }, false, 'setStatementNumberMap'),

        setParseSettings: (settings) =>
          set(
            (state) => ({
              parseSettings: { ...state.parseSettings, ...settings },
            }),
            false,
            'setParseSettings'
          ),

        reset: () =>
          set(
            {
              ...initialState,
              // Preserve persisted state
              parseSettings: get().parseSettings,
            },
            false,
            'reset'
          ),

        // Computed
        hasDocument: () => get().currentDocument !== null,
      }),
      {
        name: 'imsight-app-storage',
        // Only persist certain state
        partialize: (state) => ({
          relationsDomain: state.relationsDomain,
          parseSettings: state.parseSettings,
        }),
        // v1 drops fields nothing reads any more, among them a second copy of
        // the recent documents (person data); the rewrite removes them.
        version: 1,
        migrate: (persisted) => {
          const { relationsDomain, parseSettings } = (persisted ??
            {}) as Partial<AppState>;
          return {
            ...(relationsDomain ? { relationsDomain } : {}),
            ...(parseSettings ? { parseSettings } : {}),
          } as AppState;
        },
      }
    ),
    { name: 'AppStore', enabled: !import.meta.env.PROD }
  )
);

// Export type for external use
export type { AppState };
